import React from "react";
import { Divider, Stack, Typography } from "@mui/material";
import {
  CustomChip,
  ReuseInput,
  ReuseTooltip,
  UploadHgBlock,
} from "../../../../ui";
import { CustomButton } from "../../../../ui/CustomButton";
import { useState } from "react";
import { CIDAddedType } from "../../../../../types/apiTypes";
import { toast } from "react-toastify";
import { CustomToast } from "../../../../ui/CustomToast";
import CancelIcon from "@mui/icons-material/Cancel";
import { SubCIDBlock } from "./SubCIDBlock";

type Props = {
  isLoading: boolean;
  cids: CIDAddedType[];
  setCids: React.Dispatch<React.SetStateAction<CIDAddedType[]>>;
  isAllowedAddCid?: boolean;
};

export const UploadCidsMethod = ({
  cids,
  setCids,
  isLoading,
  isAllowedAddCid = true,
}: Props) => {
  const [cidName, setCidName] = useState<string>("");

  const addCidHandler = () => {
    if (cidName.length < 3) {
      toast.error(
        <CustomToast
          type="error"
          title="Invalid CID"
          text="The CID must contain at least 3 characters."
        />,
      );
      return;
    }

    setCids((prev) => {
      if (prev.some(({ cid }) => cid === cidName)) {
        toast.error(
          <CustomToast
            type="error"
            title="Invalid CID"
            text="Each job CID must be unique. Please choose a different CID."
          />,
        );

        return prev;
      }
      setCidName("");
      return [
        ...prev,
        { cid: cidName, isAdded: true, sub_cids: [], urls: [], files: [] },
      ];
    });
  };

  const addSubCidHandler = (
    subSid: string,
    cid: string,
    onSuccess: () => void,
  ) => {
    if (isLoading) return;

    if (subSid.length < 3) {
      toast.error(
        <CustomToast
          type="error"
          title="Invalid Sub-CID"
          text="The Sub-CID must contain at least 3 characters."
        />,
      );
      return;
    }

    setCids((prev) => {
      const state = [...prev];
      const currentCid = state.findIndex((item) => item.cid === cid);
      if (currentCid === -1) return prev;

      if (state[currentCid].sub_cids.some(({ name }) => name === subSid)) {
        toast.error(
          <CustomToast
            type="error"
            title="Invalid SubCID"
            text="Each job SubCID must be unique. Please choose a different SubCID."
          />,
        );

        return prev;
      }

      onSuccess();

      return state.map((item, idx) => {
        if (currentCid === idx) {
          return {
            ...item,
            sub_cids: [...item.sub_cids, { name: subSid, isAdded: true }],
          };
        }
        return item;
      });
    });
  };
  const removeCidHandler = (cid: string) => {
    setCids((prev) => prev.filter((item) => item.cid !== cid));
  };
  const removeSubCidHandler = (cid: string, subSid: string) => {
    setCids((prev) => {
      const state = [...prev];
      const currentCid = state.findIndex((item) => item.cid === cid);
      if (currentCid === -1) return prev;

      state[currentCid].sub_cids = state[currentCid].sub_cids.filter(
        ({ name }) => name !== subSid,
      );
      return state;
    });
  };

  const setHGDataHandler = (item: CIDAddedType) => {
    setCids((prev) =>
      prev.map((el) => {
        if (el.cid === item.cid) {
          return {
            ...el,
            ...item,
          };
        }
        return el;
      }),
    );
  };

  return (
    <Stack paddingTop={"16px"} paddingBottom={"16px"} gap={"32px"}>
      <Stack gap={"8px"} flexDirection={"row"} alignItems={"center"}>
        <ReuseInput
          isDisabled={isLoading || !isAllowedAddCid}
          name="cid"
          handleInputChange={(field, value) => setCidName(value)}
          errorMessage={""}
          value={cidName}
          label="CID"
          placeholder="AA12345"
          isError={false}
          setErrorHandler={() => {}}
        />
        <CustomButton
          onClick={addCidHandler}
          variant="outlined"
          customColor="#FFFFFF"
          textColor="#1D3E70"
          type="button"
          customSize="fit"
          disabled={!cidName || isLoading}
          sx={{
            height: "40px",
            borderColor: "#1D3E70",
            padding: "8px 16px",

            "&.Mui-disabled": {
              backgroundColor: "transparent",
              borderColor: "#EEEEEE",
            },
          }}
        >
          Add CID
        </CustomButton>
      </Stack>
      <Stack gap={"12px"}>
        {cids.map((item, index) => (
          <React.Fragment key={item.cid}>
            <Stack gap={"16px"} flexDirection={"row"} alignItems={"center"}>
              <Typography
                variant="body1"
                color="custom.textDarkBlue"
                sx={{
                  color: "#464646",
                  maxWidth: "78px",
                  width: "100%",
                }}
              >
                CID {index + 1}
              </Typography>
              <CustomChip
                label={item.cid}
                onDelete={item.isAdded ? () => {} : undefined}
                disabled={isLoading}
                sx={{
                  backgroundColor: "transparent",
                  border: "1px solid #BCBCBC",
                  fontSize: "16px",
                  lineHeight: "24px",
                  fontWeight: 500,
                  color: "#232323",
                }}
                deleteIcon={
                  <ReuseTooltip
                    text={"Removing this CID will also remove all Sub-CIDs."}
                    maxWidth={"200px"}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                  >
                    <CancelIcon
                      sx={{
                        color: "#617798",
                        width: "20px",
                        height: "20px",
                        marginRight: "6px",
                      }}
                      onClick={() => removeCidHandler(item.cid)}
                    />
                  </ReuseTooltip>
                }
              />
            </Stack>
            {(item.sub_cids.length > 0 || item.isAdded) && (
              <SubCIDBlock
                cid={item.cid}
                subCids={item.sub_cids}
                addSubCidHandler={addSubCidHandler}
                removeSubCidHandler={removeSubCidHandler}
                isAllowedAddCid={isAllowedAddCid}
                isLoading={isLoading}
              />
            )}

            <UploadHgBlock cid={item} setHGData={setHGDataHandler} />

            {index !== cids.length - 1 && <Divider sx={{ width: "100%" }} />}
          </React.Fragment>
        ))}
      </Stack>
    </Stack>
  );
};
